import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LogOut, BarChart3, Home, History, Sparkles, FilePlus2, Zap } from "lucide-react";

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItem = (to, icon, label, testid) => (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
        }`
      }
    >
      {icon}
      <span className="hidden md:inline">{label}</span>
    </NavLink>
  );

  // For public landing show minimal header
  if (!user) {
    return (
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="h-8 w-8 rounded-lg bg-slate-900 grid place-items-center">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <span className="font-display font-bold tracking-tight text-lg text-slate-900">SafeScale</span>
          </Link>
          <div className="flex items-center gap-2">
            {location.pathname !== "/login" && (
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" className="text-slate-700">Log in</Button>
              </Link>
            )}
            {location.pathname !== "/signup" && (
              <Link to="/signup" data-testid="header-signup-link">
                <Button className="bg-slate-900 hover:bg-slate-800">Start free</Button>
              </Link>
            )}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user.has_access ? "/dashboard" : "/upgrade"} className="flex items-center gap-2" data-testid="logo-link">
          <div className="h-8 w-8 rounded-lg bg-slate-900 grid place-items-center">
            <Zap className="h-4 w-4 text-white" />
          </div>
          <span className="font-display font-bold tracking-tight text-lg text-slate-900">SafeScale</span>
        </Link>
        {user.has_access ? (
          <nav className="flex items-center gap-1">
            {navItem("/dashboard", <Home className="h-4 w-4" />, "Home", "nav-dashboard")}
            {navItem("/log", <FilePlus2 className="h-4 w-4" />, "Log", "nav-log")}
            {navItem("/history", <History className="h-4 w-4" />, "History", "nav-history")}
            {navItem("/insights", <Sparkles className="h-4 w-4" />, "Insights", "nav-insights")}
          </nav>
        ) : (
          <span className="text-xs font-bold uppercase tracking-widest text-amber-700" data-testid="trial-expired-label">
            Trial ended
          </span>
        )}
        <div className="flex items-center gap-3">
          {user.is_pro ? (
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900 text-white text-[10px] font-bold tracking-widest">
              PRO
            </span>
          ) : user.in_trial ? (
            <span className="hidden sm:inline-flex text-xs font-medium text-slate-600" data-testid="trial-badge">
              Trial · {user.trial_days_left}d left
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogout}
            data-testid="logout-button"
            aria-label="Log out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
